import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { useAuth } from '../context/AuthContext';
import { 
  getRecentTransactions,
  createWithdrawalRequest
} from '../services/walletService';
import ClaimDailyLoginButton from '../components/ClaimDailyLoginButton';
import DailyCodeRedemption from '../components/DailyCodeRedemption';

const Wallet = () => {
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [upiId, setUpiId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [withdrawError, setWithdrawError] = useState("");
  const [withdrawSuccess, setWithdrawSuccess] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Use values from wallet context
  const { wallets, coinSources, withdrawWinnings, refreshWallet, isInitialized } = useWallet();
  const walletA = wallets?.walletA || 0;
  const walletB = wallets?.walletB || 0;
  
  // Get current user from auth context
  const { currentUser } = useAuth();
  
  // Conversion rate
  const conversionRate = 100; // 1 rupee = 100 coins
  
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        if (currentUser) {
          // Fetch recent transactions for the logged-in user
          const recentTransactions = await getRecentTransactions(currentUser.uid, 10);
          setTransactions(recentTransactions);
        }
      } catch (error) {
        console.error("Error loading wallet data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    if (currentUser) {
      loadData();
    }
  }, [currentUser]);
  
  // Handle manual refresh of wallet balances
  const handleRefreshWallet = async () => {
    if (!isInitialized) {
      console.log("Wallet not initialized yet, skipping refresh");
      return;
    }
    
    setRefreshing(true);
    try {
      console.log("Manual wallet refresh requested by user");
      
      // First refresh attempt
      const result = await refreshWallet();
      console.log("Wallet refreshed, current walletA:", wallets?.walletA);
      
      // Check if we need to make additional refresh attempts to restore missing sources
      if (coinSources.ads === 0 || coinSources.login === 0) {
        console.log("Detected missing wallet sources, making additional refresh attempts");
        
        // Make three more attempts with short delays between
        for (let i = 0; i < 3; i++) {
          await new Promise(resolve => setTimeout(resolve, 500));
          console.log(`Additional wallet refresh attempt #${i+1}`);
          await refreshWallet();
        }
      }
      
      // Also refresh transactions
      if (currentUser) {
        try {
          console.log("Fetching recent transactions for user:", currentUser.uid);
          const recentTransactions = await getRecentTransactions(currentUser.uid, 10);
          console.log("Fetched transactions:", recentTransactions.length);
          setTransactions(recentTransactions);
        } catch (txError) {
          console.error("Error fetching transactions:", txError);
          // Don't fail the entire refresh if just transactions fail
        }
      }
    } catch (error) {
      console.error("Error refreshing wallet data:", error);
    } finally {
      setRefreshing(false);
    }
  };

  // Run automatic recovery of rewards on initialization
  useEffect(() => {
    if (isInitialized && (coinSources.ads === 0 || coinSources.login === 0)) {
      console.log("Auto-recovery: Detected missing wallet sources, attempting recovery");
      const recoverMissingRewards = async () => {
        // Attempt multiple refreshes to recover missing rewards
        for (let i = 0; i < 3; i++) {
          await refreshWallet();
          await new Promise(resolve => setTimeout(resolve, 700));
        }
      };
      
      recoverMissingRewards();
    }
  }, [isInitialized, coinSources, refreshWallet]);

  // Handle withdrawal submission
  const handleWithdraw = async (e) => {
    e.preventDefault();
    setWithdrawError("");
    setWithdrawSuccess(false);
    
    // Validate UPI ID
    if (!upiId) {
      setWithdrawError("Please enter your UPI ID");
      return;
    }
    
    // Validate UPI ID format (basic check)
    if (!upiId.includes('@')) {
      setWithdrawError("Please enter a valid UPI ID (e.g., username@upi)");
      return;
    }
    
    // Convert rupees to coins
    const coinsToWithdraw = parseInt(withdrawAmount) * conversionRate;
    
    if (isNaN(coinsToWithdraw) || coinsToWithdraw <= 0) {
      setWithdrawError("Please enter a valid amount");
      return;
    }
    
    // Check minimum withdrawal
    if (parseInt(withdrawAmount) < 200) {
      setWithdrawError("Minimum withdrawal amount is ₹200");
      return;
    }
    
    // Check if user has enough coins
    if (coinsToWithdraw > walletB) {
      setWithdrawError("You don't have enough coins in your wallet");
      return;
    }
    
    // Process withdrawal
    try {
      setSubmitting(true);
      
      // First deduct from wallet
      const success = await withdrawWinnings(coinsToWithdraw);
      
      if (success) {
        // Then create withdrawal request
        await createWithdrawalRequest(
          currentUser.uid, 
          coinsToWithdraw, 
          'UPI', 
          { upiId: upiId }
        );
        
        setWithdrawSuccess(true);
        setWithdrawAmount("");
        setUpiId("");
      } else {
        setWithdrawError("Failed to process withdrawal. Please try again.");
      }
    } catch (error) {
      console.error("Withdrawal error:", error);
      setWithdrawError("An error occurred. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };
  
  // Transaction date formatting moved to inline usage
  
  // Get transaction icon based on type
  const getTransactionIcon = (type) => {
    switch (type) {
      case 'earn':
        return (
          <div className="p-2 bg-blue-100 rounded-full">
            <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z"></path>
              <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z"></path>
            </svg>
          </div>
        );
      case 'spend':
        return (
          <div className="p-2 bg-red-100 rounded-full">
            <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd"></path>
            </svg>
          </div>
        );
      case 'win':
        return (
          <div className="p-2 bg-green-100 rounded-full">
            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
            </svg>
          </div>
        );
      case 'withdraw':
        return (
          <div className="p-2 bg-yellow-100 rounded-full">
            <svg className="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"></path>
            </svg>
          </div>
        );
      case 'bonus':
        return (
          <div className="p-2 bg-purple-100 rounded-full">
            <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M11 17a1 1 0 001.447.894l4-2A1 1 0 0017 15V9.236a1 1 0 00-1.447-.894l-4 2a1 1 0 00-.553.894V17zM15.211 6.276a1 1 0 000-1.788l-4.764-2.382a1 1 0 00-.894 0L4.789 4.488a1 1 0 000 1.788l4.764 2.382a1 1 0 00.894 0l4.764-2.382zM4.447 8.342A1 1 0 003 9.236V15a1 1 0 00.553.894l4 2A1 1 0 009 17v-5.764a1 1 0 00-.553-.894l-4-2z"></path>
            </svg>
          </div>
        );
      case 'convert':
        return (
          <div className="p-2 bg-indigo-100 rounded-full">
            <svg className="w-4 h-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 5a1 1 0 100 2h5.586l-1.293 1.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L13.586 5H8zM12 15a1 1 0 100-2H6.414l1.293-1.293a1 1 0 10-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L6.414 15H12z"></path>
            </svg>
          </div>
        );
      case 'convert_to_b':
        return (
          <div className="p-2 bg-teal-100 rounded-full">
            <svg className="w-4 h-4 text-teal-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd"></path>
            </svg>
          </div>
        );
      default:
        return (
          <div className="p-2 bg-gray-100 rounded-full">
            <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm3 1h6v4H7V5zm6 6H7v2h6v-2z" clipRule="evenodd"></path>
            </svg>
          </div>
        );
    }
  };
  
  // Get transaction title based on type
  const getTransactionTitle = (type) => {
    switch (type) {
      case 'earn':
        return 'Earned Coins';
      case 'spend':
        return 'Spent on Entry';
      case 'win':
        return 'Won from Prediction';
      case 'withdraw':
        return 'Withdrawn Coins';
      case 'bonus':
        return 'Prediction Cycle Bonus';
      case 'convert_to_b':
        return 'Converted from Wallet A to B';
      default:
        return 'Transaction';
    }
  };
  
  // Get transaction amount color based on type
  const getAmountColorClass = (type) => {
    switch (type) {
      case 'earn':
      case 'win':
      case 'bonus':
        return 'text-green-600';
      case 'spend':
      case 'withdraw':
        return 'text-red-600';
      case 'convert_to_b':
        return 'text-teal-600';
      default:
        return 'text-gray-700';
    }
  };
  
  // Format amount for display
  const formatAmount = (amount, type) => {
    const prefix = (type === 'earn' || type === 'win' || type === 'bonus') ? '+' : '';
    return `${prefix}${amount.toLocaleString()}`;
  };
  
  // Generate mock transactions if none exist
  const generateMockTransactions = () => {
    if (transactions.length === 0) {
      const mockTypes = ['earn', 'spend', 'win', 'withdraw', 'bonus'];
      const mockTransactions = Array.from({ length: 5 }, (_, i) => ({
        id: `mock-${i}`,
        type: mockTypes[Math.floor(Math.random() * mockTypes.length)],
        amount: Math.floor(Math.random() * 5000) + 500,
        timestamp: new Date(Date.now() - i * 86400000) // past days
      }));
      return mockTransactions;
    }
    return transactions;
  };
  
  const displayTransactions = generateMockTransactions();
  
  // When the component loads, check for potential wallet inconsistencies
  useEffect(() => {
    // Delayed refresh to ensure consistency
    if (isInitialized && walletA === 0 && coinSources.login > 0) {
      console.log("Detected potential wallet inconsistency, refreshing wallet");
      
      // First refresh attempt
      refreshWallet().then(result => {
        if (result.success) {
          console.log("Successfully refreshed wallet after inconsistency detection");
        } else {
          console.error("Failed to refresh wallet:", result.error);
        }
        
        // Schedule a second refresh after a delay for extra reliability
        setTimeout(() => {
          refreshWallet().then(secondResult => {
            console.log("Second wallet refresh completed:", secondResult.success);
          }).catch(error => {
            console.error("Error during second wallet refresh:", error);
          });
        }, 2000);
      }).catch(error => {
        console.error("Error during initial wallet refresh:", error);
      });
    }
  }, [isInitialized, walletA, coinSources, refreshWallet]);
  
  // Add automatic referral rewards sync on component mount
  useEffect(() => {
    if (isInitialized && currentUser) {
      console.log("Automatic referral sync on wallet component mount");
      refreshWallet();
    }
  }, [isInitialized, currentUser, refreshWallet]);
  
  // Add another effect to automatically refresh wallet data periodically
  useEffect(() => {
    if (!isInitialized) return;
    
    // Set up an interval to refresh wallet data every 2 minutes
    const refreshInterval = setInterval(() => {
      // Only refresh if the wallet appears to be in an inconsistent state
      if (walletA === 0 && coinSources.login > 0) {
        console.log("Periodic wallet check detected inconsistency, refreshing wallet");
        refreshWallet();
      }
    }, 120000); // 2 minutes
    
    return () => clearInterval(refreshInterval);
  }, [isInitialized, walletA, coinSources, refreshWallet]);
  
  if (loading || !isInitialized) {
    return (
      <div className="p-6 flex justify-center items-center min-h-screen">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-r-2 border-indigo-600 border-opacity-50"></div>
          <span className="text-gray-600 dark:text-gray-300">Loading wallet data...</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Your Wallet</h1>
          <button 
            onClick={handleRefreshWallet}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            disabled={refreshing}
          >
            <svg 
              className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {refreshing ? 'Refreshing...' : 'Refresh Wallet'}
          </button>
        </div>
        
        {/* Daily Login Claim Button */}
        <div className="mb-6">
          <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg shadow-sm p-5 mb-2 border border-green-100 dark:border-green-800/30">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-3 flex items-center">
              <svg className="w-6 h-6 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
              Daily Login Reward
            </h2>
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              <div className="flex flex-col md:flex-row md:items-center mb-3 gap-2">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm border border-green-100 dark:border-green-900/30 flex-1">
                  <p className="text-gray-700 dark:text-gray-300 font-medium mb-1">Reward Rules:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>You must manually claim your reward every day</li>
                    <li>Rewards reset at midnight local time</li>
                  </ul>
                </div>
              </div>
            </div>
            <ClaimDailyLoginButton onRewardClaimed={handleRefreshWallet} />
            
            {/* Recent Login Rewards History */}
            <div className="mt-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <div className="bg-gray-100 dark:bg-gray-700 px-4 py-2 flex justify-between items-center">
                <h3 className="font-medium text-gray-700 dark:text-gray-200 text-sm">Recent Rewards</h3>
                <span className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-0.5 rounded-full">Last 3 days</span>
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-36 overflow-y-auto">
                {transactions.filter(t => t.type === 'login_reward').length > 0 ? (
                  transactions
                    .filter(t => t.type === 'login_reward')
                    .slice(0, 3)
                    .map((transaction, index) => (
                      <div key={index} className="px-4 py-2 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {transaction.timestamp ? new Date(transaction.timestamp).toLocaleDateString(undefined, {
                              weekday: 'short',
                              month: 'short', 
                              day: 'numeric'
                            }) : 'Unknown date'}
                          </p>
                        </div>
                        <div className="text-green-600 dark:text-green-400 font-medium text-sm">+{transaction.amount.toLocaleString()} coins</div>
                      </div>
                    ))
                ) : (
                  <div className="px-4 py-3 text-center text-gray-500 dark:text-gray-400 text-sm">
                    No rewards claimed yet
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Daily Code Redemption */}
        <div className="mb-6">
          <DailyCodeRedemption onRewardClaimed={handleRefreshWallet} />
        </div>
        
        <div className="flex items-center mb-6">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300">
            <span className="relative flex h-2 w-2 mr-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-500 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-600"></span>
            </span>
            Live Balance
          </span>
        </div>
        
        {/* Wallet Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Wallet A - Game Coins */}
          <div className="rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 dark:from-blue-800 dark:to-blue-900">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 rounded-full p-2">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 2a8 8 0 100 16 8 8 0 000-16zM5.94 5.5c.944-.945 2.56-.276 2.56 1.06V8h3v-1.44c0-1.336 1.616-2.005 2.56-1.06A4.002 4.002 0 0114 10a4.002 4.002 0 01-1.94 3.44c-.944.945-2.56.276-2.56-1.06V11H6.5v1.44c0 1.336-1.616 2.005-2.56 1.06A4.002 4.002 0 012 10a4.002 4.002 0 013.94-4.5z"></path>
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-white font-bold text-lg">Wallet A</h2>
                    <span className="bg-yellow-400 text-yellow-900 text-xs px-2 py-0.5 rounded-full font-medium">
                      Game Coins – Non-Withdrawable
                    </span>
                  </div>
                </div>
                <button type="button" className="text-white hover:bg-white/10 rounded-full p-1 transition-colors" title="Wallet Information">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-5">
              <div className="mb-4">
                <p className="text-4xl font-bold text-gray-800 dark:text-white mb-1">{walletA.toLocaleString()}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
                  </svg>
                  Game coins for prediction entries
                </p>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3 mb-4">
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"></path>
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"></path>
                    </svg>
                    <span>Earned from reading blogs, referrals & login bonuses</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"></path>
                    </svg>
                    <span>Used only for prediction entries</span>
                  </li>
                  <li className="flex items-start gap-2 text-red-500 font-medium">
                    <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <span>Cannot be withdrawn as real money</span>
                  </li>
                </ul>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
                  </svg>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="font-medium">Note:</span> Winning coins from predictions automatically go to Wallet B. Game Coins in Wallet A are used only for prediction entries.
                  </p>
                </div>
              </div>
              
              <Link to="/earn" className="inline-flex items-center justify-center w-full text-center px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd"></path>
                </svg>
                Earn More Game Coins
              </Link>
            </div>
          </div>
          
          {/* Wallet B - Withdrawable Coins */}
          <div className="rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-emerald-700 p-4 dark:from-green-800 dark:to-emerald-900">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 rounded-full p-2">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-white font-bold text-lg">Wallet B</h2>
                    <span className="bg-green-300 text-green-900 text-xs px-2 py-0.5 rounded-full font-medium">
                      Real Winnings – Withdrawable
                    </span>
                  </div>
                </div>
                <button type="button" className="text-white hover:bg-white/10 rounded-full p-1 transition-colors" title="Wallet Information">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-5">
              <div className="mb-4">
                <div className="flex items-baseline gap-2">
                  <p className="text-4xl font-bold text-gray-800 dark:text-white">{walletB.toLocaleString()}</p>
                  <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                    ≈ ₹{(walletB / conversionRate).toFixed(2)}
                  </p>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
                  </svg>
                  Withdrawable coins from prediction winnings
                </p>
              </div>
              
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 mb-4">
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <span>Only contains prediction winnings</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <span>Never includes ad, referral, login, or bonus coins</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <span>Can be withdrawn as real money via UPI</span>
                  </li>
                </ul>
                
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-green-200 dark:border-green-800/30">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Conversion Rate:</span>
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">100 coins = ₹1.00</span>
                </div>
              </div>
              
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
                  </svg>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="font-medium">Note:</span> All your winning coins from predictions automatically go to this wallet. No conversion needed.
                  </p>
                </div>
              </div>
              
              <button 
                onClick={() => document.getElementById('withdrawSection').scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center justify-center w-full text-center px-4 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd"></path>
                </svg>
                Withdraw to Bank
              </button>
            </div>
          </div>
        </div>
        
        {/* Wallet Knowledge Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-5 mb-8 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
            <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M11 17a1 1 0 001.447.894l4-2A1 1 0 0017 15V9.236a1 1 0 00-1.447-.894l-4 2a1 1 0 00-.553.894V17zM15.211 6.276a1 1 0 000-1.788l-4.764-2.382a1 1 0 00-.894 0L4.789 4.488a1 1 0 000 1.788l4.764 2.382a1 1 0 00.894 0l4.764-2.382zM4.447 8.342A1 1 0 003 9.236V15a1 1 0 00.553.894l4 2A1 1 0 009 17v-5.764a1 1 0 00-.553-.894l-4-2z"></path>
            </svg>
            How Your Wallets Work
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/10 dark:to-blue-800/20 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">Wallet A: Game Coins</h4>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2 list-disc pl-4">
                <li>Earn free coins from daily logins, reading blogs, and referring friends</li>
                <li>Use these coins to make prediction </li>
                <li>Cannot be withdrawn as real money</li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/10 dark:to-green-800/20 rounded-lg p-4">
              <h4 className="font-semibold text-green-800 dark:text-green-300 mb-2">Wallet B: Real Winnings</h4>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2 list-disc pl-4">
                <li>Only contains coins from successful predictions</li>
                <li>Never includes ad, referral, login, or bonus coins</li>
                <li>Every 100 coins = ₹1.00 (actual value)</li>
                <li>Can be withdrawn directly to your bank account via UPI</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      {/* Withdrawal Section with ID for scroll targeting */}
      <div id="withdrawSection" className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-6 border-b pb-2 flex items-center">
          <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"></path>
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"></path>
          </svg>
          Withdraw Winnings to Bank Account
        </h2>
        
        {withdrawSuccess ? (
          <div className="p-4 mb-6 text-sm text-green-700 bg-green-100 rounded-lg dark:bg-green-200 dark:text-green-800" role="alert">
            <div className="font-medium">Withdrawal Request Submitted!</div>
            <div>Your withdrawal request has been received. The amount will be credited to your account within 24-48 hours.</div>
          </div>
        ) : null}
        
        {withdrawError ? (
          <div className="p-4 mb-6 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800" role="alert">
            <div className="font-medium">Withdrawal Failed!</div>
            <div>{withdrawError}</div>
          </div>
        ) : null}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg mb-6 dark:bg-gradient-to-r dark:from-green-900/20 dark:to-emerald-900/20">
              <div className="flex items-center mb-2">
                <svg className="w-5 h-5 text-green-600 mr-2 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd"></path>
                </svg>
                <span className="text-md font-medium text-gray-700 dark:text-gray-300">Your Current Winnings</span>
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-3xl font-bold text-gray-800 dark:text-white">{walletB.toLocaleString()}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Coins in Wallet B</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-green-600 dark:text-green-400">₹{(walletB / conversionRate).toFixed(2)}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Equivalent Value</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg dark:bg-blue-900/20 dark:border-blue-800/30">
                <h3 className="font-medium text-blue-800 dark:text-blue-300 mb-2 flex items-center">
                  <svg className="w-4 h-4 mr-1.5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
                  </svg>
                  Important Information
                </h3>
                <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1">
                  <li>Minimum withdrawal amount: ₹200 (20,000 coins)</li>
                  <li>Withdrawals are processed within 24-48 hours</li>
                  <li>Direct bank transfer via UPI payment</li>
                </ul>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Request Withdrawal</h3>
              
              <form onSubmit={handleWithdraw} className="space-y-4">
                <div>
                  <label htmlFor="withdrawAmount" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                    Amount (in Rupees)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <span className="text-gray-500 dark:text-gray-400">₹</span>
                    </div>
                    <input 
                      type="number" 
                      id="withdrawAmount" 
                      min="200"
                      max={walletB / conversionRate}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block w-full pl-7 p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-green-500 dark:focus:border-green-500" 
                      placeholder="Enter amount to withdraw" 
                      value={withdrawAmount}
                      onChange={(e) => setWithdrawAmount(e.target.value)}
                      required
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {withdrawAmount ? `${(parseInt(withdrawAmount) * conversionRate).toLocaleString()} coins will be deducted from your wallet` : "Min: ₹200 | Max: ₹" + (walletB / conversionRate).toFixed(2)}
                  </p>
                </div>
                
                <div>
                  <label htmlFor="upiId" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                    UPI ID
                  </label>
                  <input 
                    type="text" 
                    id="upiId" 
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-green-500 dark:focus:border-green-500" 
                    placeholder="Enter your UPI ID (e.g. name@upi)" 
                    value={upiId}
                    onChange={(e) => setUpiId(e.target.value)}
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    We need your UPI ID to transfer the money
                  </p>
                </div>
                
                <button 
                  type="submit" 
                  className="w-full text-white bg-green-600 hover:bg-green-700 focus:ring-4 focus:outline-none focus:ring-green-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-green-500 dark:hover:bg-green-600 dark:focus:ring-green-800"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <svg className="inline w-4 h-4 mr-2 text-white animate-spin" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#E5E7EB"/>
                        <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor"/>
                      </svg>
                      Processing...
                    </>
                  ) : "Withdraw to Bank"}
                </button>
              </form>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Recent Transactions</h3>
            
            <div className="overflow-hidden">
              <div className="rounded-lg overflow-hidden shadow">
                <div className="relative">
                  {displayTransactions.length > 0 ? (
                    <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                      {displayTransactions.map((transaction, index) => (
                        <li key={transaction.id || `transaction-${index}`} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">
                          <div className="flex items-center space-x-4">
                            {getTransactionIcon(transaction.type)}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {getTransactionTitle(transaction.type)}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {transaction.timestamp ? new Date(transaction.timestamp).toLocaleString('en-IN', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : 'N/A'}
                              </p>
                            </div>
                            <div className={`inline-flex text-sm font-semibold ${getAmountColorClass(transaction.type)}`}>
                              {formatAmount(transaction.amount, transaction.type)}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="p-8 text-center">
                      <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
                      </svg>
                      <p className="mt-4 text-gray-500 dark:text-gray-400">No transactions yet</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 text-sm text-center">
                <button 
                  className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium bg-transparent border-none cursor-pointer"
                  onClick={() => alert('This feature will be available soon!')}
                >
                  View all transactions →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Wallet;